/**
 * @fileoverview Schema definitions for SKU alias records.
 * @module features/sku-alias/schemas/skuAliasSchema
 */

import { z } from 'zod'

/**
 * Branded identifier for an SKU alias row.
 */
export const skuAliasIdSchema = z.number().int().nonnegative().brand<'SkuAliasId'>()
export type SkuAliasId = z.infer<typeof skuAliasIdSchema>

/**
 * Canonical source systems for aliases.
 */
export const skuAliasSourceSchema = z.enum([
  'canonical',
  'baselinker',
  'shopify',
  'facebook_ads',
  'google_ads',
  'meta_ads_csv',
  'sales_csv',
  'internal_legacy',
])
export type SkuAliasSource = z.infer<typeof skuAliasSourceSchema>

/**
 * Branded identifier for master Variant rows.
 */
export const masterVariantIdSchema = z.number().int().nonnegative().brand<'MasterVariantId'>()
export type MasterVariantId = z.infer<typeof masterVariantIdSchema>

/**
 * Schema describing the alias mapping returned by the API.
 */
export const skuAliasSchema = z.object({
  id: skuAliasIdSchema,
  aliasSlug: z.string().min(1),
  source: skuAliasSourceSchema,
  masterVariantId: masterVariantIdSchema,
  masterSkuName: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
})

/**
 * SKU alias type derived from schema.
 *
 * @example
 * ```ts
 * const alias: SkuAlias = {
 *   id: 42 as SkuAliasId,
 *   aliasSlug: 'holi-listing',
 *   source: 'facebook_ads',
 *   masterVariantId: 7 as MasterVariantId,
 * }
 * ```
 */
export type SkuAlias = z.infer<typeof skuAliasSchema>
